import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Crown, CircleDollarSign, TrendingUp, Save, Link2, Percent } from "lucide-react";
import api from "@/lib/api";
import DashNav from "@/components/DashNav";

const EASE = [0.16, 1, 0.3, 1];

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [commission, setCommission] = useState("");
  const [freeLink, setFreeLink] = useState("");
  const [paidLink, setPaidLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [payouts, setPayouts] = useState(null);
  const [payBusy, setPayBusy] = useState("");

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setCommission(r.data.commission_percent);
      setFreeLink(r.data.free_discord_invite || "");
      setPaidLink(r.data.paid_discord_invite || "");
    }).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const saveCommission = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { commission_percent: parseFloat(commission) });
      toast.success("Commission rate updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveLinks = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { free_discord_invite: freeLink, paid_discord_invite: paidLink });
      toast.success("Discord links updated");
      load();
    } catch {
      toast.error("Failed to save links");
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (code) => {
    setPayBusy(code);
    try {
      const res = await api.post("/admin/payouts/mark-paid", { referral_code: code });
      toast.success(`Marked $${res.data.amount.toFixed(2)} paid to ${code}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark paid");
    } finally {
      setPayBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain">
      <DashNav />
      <main data-testid="admin-panel" className="max-w-6xl mx-auto px-6 py-12 space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] mb-2">Boss Office</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Admin <span className="gold-text">Control</span></h1>
        </motion.div>

        {/* Overview stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, label: "Total Members", value: overview?.total_users ?? "—", testid: "admin-stat-members" },
            { icon: Crown, label: "Active Subs", value: overview?.active_members ?? "—", testid: "admin-stat-active" },
            { icon: TrendingUp, label: "MRR", value: overview ? `$${overview.mrr.toFixed(2)}` : "—", testid: "admin-stat-mrr" },
            { icon: CircleDollarSign, label: "Commissions Owed", value: overview ? `$${overview.total_commissions.toFixed(2)}` : "—", testid: "admin-stat-commissions" },
          ].map((s) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="bg-[#101014] border border-[#26262F] rounded-xl p-5"
              data-testid={s.testid}
            >
              <s.icon className="w-4 h-4 text-[#D4AF37] mb-3" />
              <p className="font-sub font-bold text-2xl">{s.value}</p>
              <p className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Commission */}
          <div className="bg-[#101014] border border-[#26262F] rounded-2xl p-8" data-testid="admin-commission-card">
            <h2 className="font-sub font-bold text-lg mb-2 flex items-center gap-2">
              <Percent className="w-4 h-4 text-[#D4AF37]" /> Affiliate Commission
            </h2>
            <p className="text-sm text-zinc-500 mb-6">Percentage of each $15.99 membership paid to the referring affiliate. Change anytime — applies to new conversions.</p>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  data-testid="admin-commission-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                  className="w-full bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors"
                />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
              </div>
              <button
                data-testid="admin-save-commission-btn"
                onClick={saveCommission}
                disabled={saving}
                className="btn-gold flex items-center gap-2 font-sub text-sm px-6 py-3.5 rounded-full disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
            {commission !== "" && !isNaN(parseFloat(commission)) && (
              <p className="mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 mt-4">
                = ${((15.99 * parseFloat(commission)) / 100).toFixed(2)} per referral
              </p>
            )}
          </div>

          {/* Discord links */}
          <div className="bg-[#101014] border border-[#26262F] rounded-2xl p-8" data-testid="admin-links-card">
            <h2 className="font-sub font-bold text-lg mb-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-[#D4AF37]" /> Discord Invites
            </h2>
            <p className="text-sm text-zinc-500 mb-6">The waiting room link is public on the site. The syndicate link only unlocks for paid members.</p>
            <div className="space-y-4 mb-6">
              <input
                data-testid="admin-free-invite-input"
                type="url"
                value={freeLink}
                onChange={(e) => setFreeLink(e.target.value)}
                placeholder="Free waiting room invite (https://discord.gg/...)"
                className="w-full bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
              />
              <input
                data-testid="admin-paid-invite-input"
                type="url"
                value={paidLink}
                onChange={(e) => setPaidLink(e.target.value)}
                placeholder="Paid syndicate invite (https://discord.gg/...)"
                className="w-full bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
              />
            </div>
            <button
              data-testid="admin-save-links-btn"
              onClick={saveLinks}
              disabled={saving}
              className="btn-gold flex items-center gap-2 font-sub text-sm px-6 py-3.5 rounded-full disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> Save Links
            </button>
          </div>
        </div>

        {/* Affiliate payouts */}
        <div className="bg-[#101014] border border-[#26262F] rounded-2xl p-8" data-testid="admin-payouts-card">
          <h2 className="font-sub font-bold text-lg mb-2 flex items-center gap-2">
            <CircleDollarSign className="w-4 h-4 text-[#D4AF37]" /> Affiliate Payouts
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            Pay affiliates however you like (PayPal, Cash App, crypto), then mark them paid here to keep the books clean.
          </p>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-[#26262F]">
                  <th className="text-left pb-4 pr-4 font-medium">Affiliate</th>
                  <th className="text-left pb-4 pr-4 font-medium">Code</th>
                  <th className="text-left pb-4 pr-4 font-medium">Sales</th>
                  <th className="text-left pb-4 pr-4 font-medium">Unpaid</th>
                  <th className="text-left pb-4 pr-4 font-medium">Paid Out</th>
                  <th className="text-right pb-4 font-medium" />
                </tr>
              </thead>
              <tbody>
                {(payouts?.affiliates || []).map((a) => (
                  <tr key={a.referral_code} className="border-b border-[#1A1A20] last:border-0" data-testid={`payout-row-${a.referral_code}`}>
                    <td className="py-3.5 pr-4">
                      <p className="text-zinc-300">{a.name}</p>
                      <p className="text-xs text-zinc-600">{a.email}</p>
                    </td>
                    <td className="py-3.5 pr-4 mono text-xs text-[#E6C665]">{a.referral_code}</td>
                    <td className="py-3.5 pr-4 text-zinc-400">{a.conversions}</td>
                    <td className="py-3.5 pr-4 font-sub font-bold text-[#E6C665]">${a.unpaid.toFixed(2)}</td>
                    <td className="py-3.5 pr-4 text-zinc-500">${a.paid.toFixed(2)}</td>
                    <td className="py-3.5 text-right">
                      <button
                        data-testid={`mark-paid-btn-${a.referral_code}`}
                        onClick={() => markPaid(a.referral_code)}
                        disabled={a.unpaid <= 0 || payBusy === a.referral_code}
                        className="btn-ghost-gold text-xs font-sub px-4 py-2 rounded-full disabled:opacity-40"
                      >
                        {payBusy === a.referral_code ? "..." : "Mark Paid"}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!payouts || payouts.affiliates.length === 0) && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-zinc-600 text-sm">No affiliate commissions yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {(payouts?.history || []).length > 0 && (
            <div>
              <p className="mono text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-4">Payout History</p>
              <div className="space-y-2">
                {payouts.history.map((p) => (
                  <div key={p.payout_id} className="flex items-center justify-between gap-4 chip rounded-lg px-4 py-3" data-testid={`payout-history-${p.payout_id}`}>
                    <span className="mono text-xs text-[#E6C665]">{p.referral_code}</span>
                    <span className="text-xs text-zinc-500">{p.conversions} sale{p.conversions !== 1 ? "s" : ""}</span>
                    <span className="font-sub font-bold text-sm">${p.amount.toFixed(2)}</span>
                    <span className="text-xs text-zinc-600">{new Date(p.paid_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Users table */}
        <div className="bg-[#101014] border border-[#26262F] rounded-2xl p-8" data-testid="admin-users-card">
          <h2 className="font-sub font-bold text-lg mb-6">Members & Affiliates</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-[#26262F]">
                  <th className="text-left pb-4 pr-4 font-medium">Email</th>
                  <th className="text-left pb-4 pr-4 font-medium">Name</th>
                  <th className="text-left pb-4 pr-4 font-medium">Status</th>
                  <th className="text-left pb-4 pr-4 font-medium">Ref Code</th>
                  <th className="text-left pb-4 pr-4 font-medium">Referred By</th>
                  <th className="text-left pb-4 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#1A1A20] last:border-0" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="py-3.5 pr-4 text-zinc-300">{u.email}</td>
                    <td className="py-3.5 pr-4 text-zinc-400">{u.name}{u.is_admin ? " ★" : ""}</td>
                    <td className="py-3.5 pr-4">
                      <span className={`mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full ${
                        u.membership_status === "active"
                          ? "bg-[rgba(212,175,55,0.12)] text-[#E6C665]"
                          : "bg-[#15151A] text-zinc-500"
                      }`}>
                        {u.membership_status || "none"}
                      </span>
                    </td>
                    <td className="py-3.5 pr-4 mono text-xs text-[#E6C665]">{u.referral_code}</td>
                    <td className="py-3.5 pr-4 mono text-xs text-zinc-500">{u.referred_by || "—"}</td>
                    <td className="py-3.5 text-zinc-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-zinc-600 text-sm">No members yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
